import { ComponentProps, FunctionComponent } from 'react';
import { Tag } from '@tocktix/pantry';

const EM_DASH = '\u2014';

type TagColor = ComponentProps<typeof Tag>['color'];
type TagIcon = ComponentProps<typeof Tag>['IconStart'];

/** A label/color (and optional leading icon) used to render a status or source value as a Tag. */
export type StatusDisplay = { label: string; color: TagColor; IconStart?: TagIcon };

type StatusTagProps = {
  value?: string;
  displayMap: Record<string, StatusDisplay>;
  'data-testid'?: string;
};

/**
 * Renders a small pantry Tag for a raw status/source string using `displayMap`. Unknown non-empty
 * values fall back to a secondary-colored tag labeled with the raw value; empty/undefined values
 * render an em-dash placeholder.
 */
const StatusTag: FunctionComponent<StatusTagProps> = ({
  value,
  displayMap,
  'data-testid': dataTestId,
}) => {
  if (!value) {
    return <>{EM_DASH}</>;
  }
  const display: StatusDisplay = displayMap[value] ?? { label: value, color: 'secondary' };
  return (
    <Tag
      label={display.label}
      color={display.color}
      size="sm"
      IconStart={display.IconStart}
      data-testid={dataTestId}
    />
  );
};

export default StatusTag;
